/**
 * EPikChart — Government e-Pik Pahani (2024-25) data visualisation.
 * Converts the tabular Mosambi farmer distribution into charts as per
 * Phase 1 Execution Brief items 12 (Home) & 2 (About). Source label is
 * mandatory on every rendering — never omit.
 */
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { Landmark } from "lucide-react";

const TALUKA_DATA = [
  { name: "Ambad", farmers: 6922, share: 38.3 },
  { name: "Ghansawangi", farmers: 4178, share: 23.1 },
  { name: "Badnapur", farmers: 3345, share: 18.5 },
  { name: "Jalna", farmers: 2950, share: 16.3 },
  { name: "Partur", farmers: 318, share: 1.8 },
  { name: "Bhokardan", farmers: 184, share: 1.0 },
  { name: "Mantha", farmers: 131, share: 0.7 },
  { name: "Jafrabad", farmers: 61, share: 0.3 },
];

const VILLAGE_DATA = [
  { name: "Rajewadi", taluka: "Badnapur", farmers: 258 },
  { name: "Eklahera", taluka: "Ambad", farmers: 236 },
  { name: "Bhardi", taluka: "Ambad", farmers: 230 },
  { name: "Karjat", taluka: "Ambad", farmers: 213 },
  { name: "Rohilagad", taluka: "Ambad", farmers: 202 },
];

const BAR_COLORS = ["#064E3B", "#0a6b52", "#0f8266", "#149878", "#84CC16", "#a3d13d", "#c1d668", "#dcda94"];

export default function EPikChart({ variant = "light" }) {
  const { t } = useTranslation();
  const dark = variant === "dark";
  const axisColor = dark ? "rgba(255,255,255,0.75)" : "#064E3B";
  const gridBg = dark ? "bg-white/[0.04] border-white/10" : "bg-white border-[rgba(6,78,59,0.08)]";
  const cardText = dark ? "text-white" : "text-[var(--tr-deep-green)]";
  const muted = dark ? "text-white/70" : "text-[var(--tr-muted)]";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="epik-chart-block">
      {/* Taluka distribution — horizontal bars, sorted desc */}
      <div className={`rounded-3xl border p-6 md:p-8 ${gridBg}`} data-testid="epik-chart-talukas">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className={`text-[11px] uppercase tracking-[0.22em] font-bold ${dark ? "text-[var(--tr-citrus-yellow)]" : "text-[var(--tr-zest)]"}`}>
              {t("epik.chart_talukas_eyebrow")}
            </div>
            <h3 className={`mt-2 font-display font-extrabold text-xl md:text-2xl ${cardText}`}>
              {t("epik.chart_talukas_title")}
            </h3>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${dark ? "bg-[var(--tr-citrus-yellow)] text-[var(--tr-deep-green)]" : "bg-[var(--tr-lime)]/40 text-[var(--tr-deep-green)]"}`}>
            <Landmark className="w-3 h-3" /> Govt · e-Pik Pahani
          </span>
        </div>

        <div className="mt-6 h-[340px] w-full" data-testid="epik-chart-talukas-svg">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={TALUKA_DATA} layout="vertical" margin={{ top: 4, right: 40, left: 4, bottom: 4 }}>
              <XAxis type="number" hide domain={[0, "dataMax"]} />
              <YAxis type="category" dataKey="name" width={92} tick={{ fill: axisColor, fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: dark ? "rgba(255,255,255,0.06)" : "rgba(6,78,59,0.05)" }}
                contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 10px 30px rgba(6,78,59,0.15)", fontSize: 12 }}
                formatter={(v, _, item) => [`${v.toLocaleString()} farmers · ${item?.payload?.share}%`, "Registered"]}
              />
              <Bar dataKey="farmers" radius={[0, 8, 8, 0]}>
                {TALUKA_DATA.map((_, i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
                <LabelList dataKey="share" position="right" formatter={(v) => `${v}%`} style={{ fill: axisColor, fontSize: 11, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <p className={`mt-4 text-xs italic leading-relaxed ${muted}`} data-testid="epik-chart-source-1">
          {t("epik.source_label")}
        </p>
      </div>

      {/* Top villages */}
      <div className={`rounded-3xl border p-6 md:p-8 ${gridBg}`} data-testid="epik-chart-villages">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className={`text-[11px] uppercase tracking-[0.22em] font-bold ${dark ? "text-[var(--tr-citrus-yellow)]" : "text-[var(--tr-zest)]"}`}>
              {t("epik.chart_villages_eyebrow")}
            </div>
            <h3 className={`mt-2 font-display font-extrabold text-xl md:text-2xl ${cardText}`}>
              {t("epik.chart_villages_title")}
            </h3>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${dark ? "bg-[var(--tr-citrus-yellow)] text-[var(--tr-deep-green)]" : "bg-[var(--tr-lime)]/40 text-[var(--tr-deep-green)]"}`}>
            <Landmark className="w-3 h-3" /> Govt · e-Pik Pahani
          </span>
        </div>

        <div className="mt-6 h-[340px] w-full" data-testid="epik-chart-villages-svg">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={VILLAGE_DATA} margin={{ top: 20, right: 12, left: 4, bottom: 4 }}>
              <XAxis dataKey="name" tick={{ fill: axisColor, fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 300]} />
              <Tooltip
                cursor={{ fill: dark ? "rgba(255,255,255,0.06)" : "rgba(6,78,59,0.05)" }}
                contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 10px 30px rgba(6,78,59,0.15)", fontSize: 12 }}
                formatter={(v, _, item) => [`${v} farmers`, `${item?.payload?.name} · ${item?.payload?.taluka} taluka`]}
              />
              <Bar dataKey="farmers" radius={[10, 10, 0, 0]}>
                {VILLAGE_DATA.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? "#F97316" : "#064E3B"} />
                ))}
                <LabelList dataKey="farmers" position="top" style={{ fill: axisColor, fontSize: 11, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <p className={`mt-4 text-xs italic leading-relaxed ${muted}`} data-testid="epik-chart-source-2">
          {t("epik.villages_note")}
        </p>
      </div>
    </div>
  );
}
